import { Radio, styled } from "@mui/material";
import React from "react";
import AcxSelectSingleAsync, {
    IAcxSelectSingleAsync,
} from "./BaseSelectComponents/AcxSelectSingleAsync";
import { OptionTypeBase } from "react-select";

const ControlContainer = styled("div")(() => ({
    display: "grid",
    gridTemplateColumns: "1fr 3fr 3fr",
    alignItems: "center",
    margin: "0 0 0.5rem 0",
    padding: "0px",
    gap: "0.5rem",
}));

const Label = styled("label")(({ theme }) => ({
    userSelect: "none",
    fontSize: "12px",
    lineHeight: "16px",
    color: theme.palette.text.secondary,
    fontFamily: theme.typography.fontFamily,
    fontWeight: theme.typography.fontWeightBold as any,
}));

const ItemContainer = styled("section")(({ theme }) => ({
    display: "flex",
    gap: "0.25rem",
    alignItems: "center",
}));

const Border = styled("div")(({ theme }) => ({
    border: `1px solid ${theme.palette.lightgrayBorder.main}`,
    borderRadius: "3px",
    width: "100%",
}));

interface IAcxLabeledSelectAsyncToggle<
    T extends OptionTypeBase = IAcxLabeledSelectToggleOption,
> extends Omit<
        IAcxSelectSingleAsync<T>,
        "options" | "isClearable" | "isLoading"
    > {
    readonly fieldIdentifierLabel?: string;
    setRequired?: boolean;
    readonly fieldName: string;
    firstDropdownValue: T;
    secondDropdownValue: T;
}
interface IAcxLabeledSelectToggleOption {
    id: string;
    label: string;
}

const AcxLabeledSelectAsyncToggle = <
    T extends OptionTypeBase = IAcxLabeledSelectToggleOption,
>(
    props: IAcxLabeledSelectAsyncToggle<T>,
) => {
    const [checked, setChecked] = React.useState("");
    const [valueOne, setValueOne] = React.useState(props.firstDropdownValue);
    const [valueTwo, setValueTwo] = React.useState(props.secondDropdownValue);

    React.useEffect(() => {
        if (checked === `firstRadioField_${props.fieldName}`) {
            props.onChange?.(valueOne);
        } else if (checked === `secondRadioField_${props.fieldName}`) {
            props.onChange?.(valueTwo);
        }
    }, [props, checked, valueOne, valueTwo]);

    React.useEffect(() => {
        if (props.firstDropdownValue) {
            setChecked(`firstRadioField_${props.fieldName}`);
        } else if (!props.firstDropdownValue && props.secondDropdownValue) {
            setChecked(`secondRadioField_${props.fieldName}`);
        } else {
            setChecked(`firstRadioField_${props.fieldName}`);
        }
    }, [props, props.fieldName]);

    const handleOneInputUpdate = (arg: T | undefined) => {
        setValueOne(arg as T);
    };

    const handleTwoInputUpdate = (arg: T | undefined) => {
        setValueTwo(arg as T);
    };

    const handleRadioUpdate = (event: React.ChangeEvent<HTMLInputElement>) => {
        setChecked(event.target.id);
    };

    return (
        <ControlContainer>
            <Label title={props.fieldIdentifierLabel}>
                {props.fieldIdentifierLabel}
            </Label>

            <ItemContainer>
                <Radio
                    key={`firstRadioField_${props.fieldName}`}
                    id={`firstRadioField_${props.fieldName}`}
                    checked={
                        checked === `firstRadioField_${props.fieldName}`
                            ? true
                            : false
                    }
                    onChange={handleRadioUpdate}
                    name={props.fieldName}
                    value={valueOne ? JSON.stringify(valueOne) : ""}
                />

                <Border>
                    <AcxSelectSingleAsync
                        key={`firstTextField_${props.fieldName}`}
                        id={`firstTextField_${props.fieldName}`}
                        containerHeight="auto"
                        loadOptions={props.loadOptions}
                        defaultValue={valueOne}
                        valueField={props.valueField}
                        labelField={props.labelField}
                        required={props.setRequired ? props.setRequired : false}
                        noOptionMessage={props.noOptionMessage}
                        onChange={handleOneInputUpdate}
                        customStyle={{
                            dropdownIndicator: () => ({
                                opacity: "0%",
                            }),
                            singleValue: () => ({
                                opacity: "100%",
                            }),
                        }}
                        isDisabled
                        placeholder=""
                    />
                </Border>
            </ItemContainer>

            <ItemContainer>
                <Radio
                    key={`secondRadioField_${props.fieldName}`}
                    id={`secondRadioField_${props.fieldName}`}
                    checked={
                        checked === `secondRadioField_${props.fieldName}`
                            ? true
                            : false
                    }
                    onChange={handleRadioUpdate}
                    name={props.fieldName}
                    value={valueTwo ? JSON.stringify(valueTwo) : ""}
                />

                <Border>
                    <AcxSelectSingleAsync
                        key={`secondTextField_${props.fieldName}`}
                        id={`secondTextField_${props.fieldName}`}
                        containerHeight="auto"
                        loadOptions={props.loadOptions}
                        cacheOptions
                        isClearable
                        defaultValue={valueTwo}
                        valueField={props.valueField}
                        labelField={props.labelField}
                        required={props.setRequired ? props.setRequired : false}
                        noOptionMessage={props.noOptionMessage}
                        onChange={handleTwoInputUpdate}
                        customStyle={{
                            dropdownIndicator: () => ({
                                opacity: "0%",
                            }),
                            singleValue: () => ({
                                opacity: "100%",
                            }),
                        }}
                        isDisabled
                        placeholder=""
                    />
                </Border>
            </ItemContainer>
        </ControlContainer>
    );
};

export default AcxLabeledSelectAsyncToggle;
